/**
 * El primer paso de toda venta: a quien se le vende.
 *
 * POR QUE ES UN PASO Y NO UN BOTON OPCIONAL
 * -----------------------------------------
 * Antes el cliente era un boton "Asignar cliente" arriba del ticket. Nada lo
 * pedia y nada lo recordaba, asi que la venta a un cliente que YA existe en el
 * sistema salia igual sin asignar — y una venta emitida al vacio pierde la
 * cuenta corriente, el historial y el extracto de esa persona.
 *
 * Hay ademas un motivo que no es de prolijidad sino de trabajo perdido:
 * `emitir_venta` RECHAZA la venta si el cliente excede su limite de credito
 * (0011_correcciones.sql). Con el cliente al final, el rechazo llega recien al
 * apretar Cobrar, con el ticket entero ya cargado. Preguntando primero, el
 * limite y el saldo se ven antes de tipear el primer producto.
 *
 * EL CAMINO RAPIDO NO SE PENALIZA
 * -------------------------------
 * La venta de mostrador es la mas frecuente y no puede costar mas que antes.
 * Las dos respuestas se dan sin tocar el mouse:
 *
 *   Enter            -> mostrador, y el campo de productos queda enfocado.
 *   cualquier letra  -> abre el buscador de clientes CON esa letra ya escrita.
 *
 * Las dos escuchan en `window` y no en el boton. Tenerlo en el `autoFocus` del
 * boton alcanzaba solo para el primer instante: un clic en cualquier parte de
 * la pantalla se lleva el foco y el Enter dejaba de hacer nada, que es
 * exactamente como se rompe en el mostrador de verdad.
 *
 * Y "Mostrador" no es lo mismo que no contestar. Queda guardado en el ticket
 * como una respuesta explicita: la venta sale sin cliente porque alguien lo
 * decidio, no porque el sistema no pregunto.
 */
import { useEffect } from "react";
import { Store, UserPlus } from "lucide-react";

import { cn } from "@/lib/utils";

export function PasoDestinatario({
  onMostrador,
  onCliente,
}: {
  onMostrador: () => void;
  /** `textoInicial` es la tecla que disparo la apertura: no se pierde. */
  onCliente: (textoInicial?: string) => void;
}): JSX.Element {
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      // Los atajos del sistema y del navegador siguen siendo suyos.
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;

      const objetivo = e.target as HTMLElement | null;
      const etiqueta = objetivo?.tagName;

      // Si hay un campo escuchando —el buscador ⌘K, el selector de clientes—
      // lo que se tipea es para el, no para esta pantalla.
      if (
        etiqueta === "INPUT" ||
        etiqueta === "TEXTAREA" ||
        etiqueta === "SELECT" ||
        objetivo?.isContentEditable
      ) {
        return;
      }
      // Idem si hay un dialogo abierto encima.
      if (document.querySelector('[role="dialog"]')) return;

      if (e.key === "Enter") {
        // Con el foco en uno de los dos botones gana el click nativo: si no,
        // "Buscar cliente" enfocado + Enter terminaria eligiendo mostrador.
        if (etiqueta === "BUTTON") return;
        e.preventDefault();
        onMostrador();
        return;
      }

      // Una letra, un numero o un acento: es alguien empezando a escribir un
      // nombre. Se abre la busqueda de clientes con esa tecla ya puesta.
      // El espacio queda afuera a proposito: activa el boton enfocado.
      if (e.key.length === 1 && e.key !== " ") {
        e.preventDefault();
        onCliente(e.key);
      }
    };

    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [onMostrador, onCliente]);

  // Tarjeta-opcion: dos <button> crudos a proposito. No son un Button (son una
  // tarjeta entera con icono, titulo y atajo) ni un radiogroup (no hay un
  // estado elegido que persista: tocar una ya resuelve el paso).
  const base = cn(
    "flex min-h-[13rem] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border p-6",
    "transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 sm:w-72",
  );

  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center">
      {/*
        Sin titulo ni bajada: las dos tarjetas dicen solas de que se trata, y
        un encabezado que pregunta lo que las opciones ya contestan es un
        renglon que se saltea treinta veces por dia. El porque del paso esta
        documentado arriba, en el archivo, que es donde le sirve a alguien.

        Cada tarjeta anuncia su atajo. Un atajo que no se escribe no lo usa
        nadie, y estos dos son los que hacen que el paso no cueste.
      */}
      <div className="flex w-full max-w-2xl flex-col items-center gap-4 sm:flex-row sm:justify-center">
        {/* boton crudo a proposito: tarjeta-opcion (ver `base`) */}
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- es la accion mas
            frecuente del dia y el motivo entero de que el paso no moleste:
            Enter y seguis. */}
        {/* <button> crudo: tarjeta-opcion (ver `base`). */}
        <button
          type="button"
          autoFocus
          onClick={onMostrador}
          className={cn(base, "border-border bg-card hover:border-ring hover:bg-accent")}
        >
          <Store className="size-8 text-muted-foreground" aria-hidden="true" />
          <span className="text-base font-semibold text-foreground">Mostrador</span>
          <span className="text-sm text-muted-foreground">
            Sin cliente
            {/* Pistas del teclado de la compu: en el celular no hay Enter. */}
            <span className="max-sm:hidden">
              {" "}
              · <kbd className="font-sans">Enter</kbd>
            </span>
          </span>
        </button>

        {/* boton crudo a proposito: tarjeta-opcion (ver `base`) */}
        <button
          type="button"
          onClick={() => onCliente()}
          className={cn(base, "border-dashed border-input hover:border-ring hover:bg-accent")}
        >
          <UserPlus className="size-8 text-muted-foreground" aria-hidden="true" />
          <span className="text-base font-semibold text-foreground">Buscar cliente</span>
          <span className="text-sm text-muted-foreground">
            Cuenta corriente<span className="max-sm:hidden"> · escribí</span>
          </span>
        </button>
      </div>
    </div>
  );
}
